import {
  forwardRef,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

/**
 * The single source of truth for form control styling.
 *
 * This string previously existed as ~30 hand-copied variants across the app,
 * which is how `dark:bg-gray-800` and `dark:bg-gray-700` ended up on inputs
 * sitting next to each other.
 */
const base =
  'w-full px-3 py-2 rounded-md border text-sm bg-white dark:bg-gray-800 ' +
  'text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 ' +
  'transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

const valid = 'border-gray-300 dark:border-gray-600';
const invalidBorder = 'border-status-down dark:border-status-down';

export function controlClass(invalid: boolean, extra = ''): string {
  return `${base} ${invalid ? invalidBorder : valid} ${extra}`;
}

/**
 * `Field` already supplies `aria-invalid`, so the control derives its own
 * error styling from that rather than needing a second prop kept in sync.
 */
function isInvalid(v: unknown): boolean {
  return v === true || v === 'true';
}

type InputProps = InputHTMLAttributes<HTMLInputElement>;
type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;
type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className = '', ...props }, ref) => (
    <input ref={ref} className={controlClass(isInvalid(props['aria-invalid']), className)} {...props} />
  ),
);
Input.displayName = 'Input';

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className = '', ...props }, ref) => (
    <select ref={ref} className={controlClass(isInvalid(props['aria-invalid']), className)} {...props} />
  ),
);
Select.displayName = 'Select';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className = '', ...props }, ref) => (
    <textarea ref={ref} className={controlClass(isInvalid(props['aria-invalid']), className)} {...props} />
  ),
);
Textarea.displayName = 'Textarea';

/** Checkboxes are small enough to style inline, but still from one place. */
export const checkboxClass =
  'rounded border-gray-300 dark:border-gray-600 text-brand-600 ' +
  'focus:ring-2 focus:ring-brand-500 focus:ring-offset-0';
